import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { Category } from "../../../types/category";
import { createCategory } from "../../../State/admin/Action";
import { fetchAllCategories } from "../../../State/customer/categorySlice";

interface CategoryFormData {
    name: string;
    categoryId: string;
    level: number;
    parentCategoryId: string;
}

const AddCategoryForm: React.FC = () => {
    const dispatch = useAppDispatch();
    const categoryState = useAppSelector((state) => state.category);

    const categories: Category[] = categoryState?.categories || [];
    const loading = categoryState?.loading || false;

    const [formData, setFormData] = useState<CategoryFormData>({
        name: "",
        categoryId: "",
        level: 1,
        parentCategoryId: "",
    });

    const [selectedLevel1Parent, setSelectedLevel1Parent] = useState<string>("");
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Fetch categories on mount if not already loaded
    useEffect(() => {
        dispatch(fetchAllCategories());
    }, [dispatch]);

    // Derived lists for dynamic parent selects
    const level1Categories = categories.filter((c) => c.level === 1);

    // Dynamic filtering for Level 2 options
    const level2Categories = categories.filter((c) => {
        if (c.level !== 2) return false;
        if (formData.level === 3 && selectedLevel1Parent) {
            return c.parentCategoryId === selectedLevel1Parent;
        }
        return true;
    });

    // Auto-generate URL slug / categoryId from category name
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nameVal = e.target.value;
        const generatedSlug = nameVal
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s_]/g, "")
            .replace(/\s+/g, "_");

        setFormData((prev) => ({
            ...prev,
            name: nameVal,
            categoryId: generatedSlug,
        }));

        if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSelectChange = (e: SelectChangeEvent<number | string>) => {
        const { name, value } = e.target;

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        if (name === "level") {
            const newLevel = Number(value);
            setFormData((prev) => ({
                ...prev,
                level: newLevel,
                parentCategoryId: "", // Reset parent when level changes
            }));
            setSelectedLevel1Parent("");
        } else if (name === "level1Filter") {
            setSelectedLevel1Parent(String(value));
            setFormData((prev) => ({ ...prev, parentCategoryId: "" }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: String(value),
            }));
        }

        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) {
            newErrors.name = "Category name is required";
        }
        if (!formData.categoryId.trim()) {
            newErrors.categoryId = "Category ID (slug) is required";
        }
        if (formData.level > 1 && !formData.parentCategoryId) {
            newErrors.parentCategoryId = `Parent category is required for Level ${formData.level}`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuccessMessage(null);

        if (!validateForm()) return;

        try {
            await dispatch(createCategory(formData)).unwrap();
            setSuccessMessage(`Successfully created category "${formData.name}"!`);

            // Reset form (Redux state is automatically updated via createCategory.fulfilled)
            setFormData({
                name: "",
                categoryId: "",
                level: 1,
                parentCategoryId: "",
            });
            setSelectedLevel1Parent("");
            setErrors({});
        } catch (err: any) {
            setErrors({ submit: typeof err === "string" ? err : "Failed to create category" });
        }
    };

    return (
        <Card sx={{ maxWidth: 600, mx: "auto", mt: 4, p: 2, boxShadow: 3 }}>
            <CardContent>
                <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
                    Create New Category
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                    Add top-level categories, subcategories, or specific item types to your catalog.
                </Typography>

                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMessage}
                    </Alert>
                )}

                {errors.submit && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errors.submit}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    {/* Category Level Selection */}
                    <FormControl fullWidth margin="normal" error={!!errors.level}>
                        <InputLabel id="level-label">Category Level</InputLabel>
                        <Select
                            labelId="level-label"
                            name="level"
                            value={formData.level}
                            label="Category Level"
                            onChange={handleSelectChange}
                        >
                            <MenuItem value={1}>Level 1 — Main Category (e.g., Men, Women)</MenuItem>
                            <MenuItem value={2}>Level 2 — Subcategory (e.g., Topwear, Shoes)</MenuItem>
                            <MenuItem value={3}>Level 3 — Item Category (e.g., T-Shirts, Sneakers)</MenuItem>
                        </Select>
                        <FormHelperText>
                            {formData.level === 1
                                ? "Creates a root navigation item."
                                : formData.level === 2
                                ? "Requires selecting a Level 1 parent."
                                : "Requires selecting a Level 2 parent."}
                        </FormHelperText>
                    </FormControl>

                    {/* Conditional Parent Selection for Level 2 */}
                    {formData.level === 2 && (
                        <FormControl fullWidth margin="normal" error={!!errors.parentCategoryId}>
                            <InputLabel id="parent-l1-label">Select Level 1 Parent Category</InputLabel>
                            <Select
                                labelId="parent-l1-label"
                                name="parentCategoryId"
                                value={formData.parentCategoryId}
                                label="Select Level 1 Parent Category"
                                onChange={handleSelectChange}
                            >
                                {level1Categories.map((cat) => (
                                    <MenuItem key={cat.categoryId} value={cat.categoryId}>
                                        {cat.name} ({cat.categoryId})
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.parentCategoryId && (
                                <FormHelperText>{errors.parentCategoryId}</FormHelperText>
                            )}
                        </FormControl>
                    )}

                    {/* Conditional Parent Selection for Level 3 */}
                    {formData.level === 3 && (
                        <>
                            {/* Filter Level 2 options by Level 1 Parent */}
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="filter-l1-label">Filter by Level 1 Main Category</InputLabel>
                                <Select
                                    labelId="filter-l1-label"
                                    name="level1Filter"
                                    value={selectedLevel1Parent}
                                    label="Filter by Level 1 Main Category"
                                    onChange={handleSelectChange}
                                >
                                    <MenuItem value="">All Level 1 Categories</MenuItem>
                                    {level1Categories.map((cat) => (
                                        <MenuItem key={cat.categoryId} value={cat.categoryId}>
                                            {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Select Level 2 Parent */}
                            <FormControl fullWidth margin="normal" error={!!errors.parentCategoryId}>
                                <InputLabel id="parent-l2-label">Select Level 2 Parent Category</InputLabel>
                                <Select
                                    labelId="parent-l2-label"
                                    name="parentCategoryId"
                                    value={formData.parentCategoryId}
                                    label="Select Level 2 Parent Category"
                                    onChange={handleSelectChange}
                                >
                                    {level2Categories.map((cat) => (
                                        <MenuItem key={cat.categoryId} value={cat.categoryId}>
                                            {cat.name} (Parent: {cat.parentCategoryId})
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.parentCategoryId && (
                                    <FormHelperText>{errors.parentCategoryId}</FormHelperText>
                                )}
                            </FormControl>
                        </>
                    )}

                    {/* Category Display Name */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Category Name"
                        name="name"
                        value={formData.name}
                        onChange={handleNameChange}
                        error={!!errors.name}
                        helperText={errors.name || "e.g., Running Shoes"}
                        required
                    />

                    {/* Category Unique Slug / ID */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Category ID (Slug)"
                        name="categoryId"
                        value={formData.categoryId}
                        onChange={handleInputChange}
                        error={!!errors.categoryId}
                        helperText={
                            errors.categoryId || "Unique Identifier used in URLs (auto-generated)"
                        }
                        required
                    />

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{ mt: 3, py: 1.5, fontWeight: "bold" }}
                    >
                        {loading ? <CircularProgress size={26} color="inherit" /> : "Save Category"}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default AddCategoryForm;